import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

type Language = 'fr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

interface LanguageProviderProps {
  children: ReactNode;
  userId?: string | null;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children, userId = null }: LanguageProviderProps) {
  const { i18n } = useTranslation();

  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage first
    const saved = localStorage.getItem('language');
    if (saved && ['fr', 'en'].includes(saved)) {
      return saved as Language;
    }
    // Fall back to French as default (per user preference)
    return 'fr';
  });

  // Sync i18n language when state changes
  useEffect(() => {
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  }, [language, i18n]);

  // Persist to Firestore when user is authenticated
  useEffect(() => {
    if (userId) {
      const updateUserLanguage = async () => {
        try {
          await updateDoc(doc(db, 'users', userId), { language });
        } catch (error) {
          // Silently fail - localStorage is the primary storage
          console.error('Failed to persist language to Firestore:', error);
        }
      };
      updateUserLanguage();
    }
  }, [language, userId]);

  const setLanguage = (lang: Language) => {
    if (['fr', 'en'].includes(lang)) {
      setLanguageState(lang);
    }
  };

  const toggleLanguage = () => {
    setLanguageState((prev) => (prev === 'fr' ? 'en' : 'fr'));
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
